'use strict';
import React, { useState } from 'react';
import { X, Printer, Copy, Check, ExternalLink } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { feeUnit } from '../../utils/helpers';

const fmt = (dateStr) => {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
};

const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${period}`;
};

function DetailRow({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: '12px', padding: '10px 0', borderBottom: '1px solid #f1f5f9' }}>
      <span style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', minWidth: '120px', paddingTop: '1px' }}>{label}</span>
      <span style={{ fontSize: '13px', color: '#1e293b', fontWeight: 500 }}>{value}</span>
    </div>
  );
}

export default function FlyerModal({ isOpen, onClose, type = 'program', data = {}, academy = {}, academyId }) {
  const { theme } = useTheme();
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const isProgram = type === 'program';
  const title = data.name || (isProgram ? 'Program' : 'Class');
  const sport  = data.sportName || data.sport || null;
  const level  = data.level ? data.level.charAt(0).toUpperCase() + data.level.slice(1) : null;

  const startDate = fmt(data.startDate);
  const endDate   = fmt(data.endDate);
  const dateRange = startDate && endDate ? `${startDate} – ${endDate}` : startDate || endDate || null;

  const schedule = data.timeFrom && data.timeTo
    ? `${fmtTime(data.timeFrom)} – ${fmtTime(data.timeTo)}`
    : null;

  const fee = data.fee != null ? `$${Number(data.fee).toFixed(0)}${feeUnit(data, { long: true })}` : null;
  const regDeadline = fmt(data.registrationDueDate);
  const lateFee = data.lateFee != null ? `$${Number(data.lateFee).toFixed(0)} after deadline` : null;
  const capacity = data.maxParticipants
    ? `${data.enrollmentCount ?? 0} / ${data.maxParticipants} enrolled`
    : null;

  const academyName    = academy.name    || 'Academy';
  const academyWebsite = academy.website || null;

  // Program-level contact takes priority over academy contact
  const contactName  = data.contactName  || null;
  const contactEmail = data.contactEmail || null;
  const contactPhone = data.contactPhone || null;

  const ctaUrl = academyId && data.id
    ? `${window.location.origin}/exsports/academy/${academyId}?tab=programs&program=${data.id}`
    : academyWebsite || '#';

  const regOpen = data.registrationOpen !== false;

  const handleCopy = () => {
    navigator.clipboard?.writeText(ctaUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // ── Accent colour based on sport / level ──────────────────────────────────
  const accent = '#2563eb';

  return (
    <>
      <style>{`
        @media print {
          body > * { display: none !important; }
          .flyer-print-root { display: block !important; position: fixed !important; inset: 0; background: white; padding: 0; z-index: 99999; }
          .flyer-no-print { display: none !important; }
        }
      `}</style>

      <div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/65"
      >
        <div className={`${theme.card} rounded-2xl w-full max-w-lg max-h-[95vh] overflow-y-auto shadow-2xl`}>

          {/* Toolbar */}
          <div className={`flyer-no-print sticky top-0 z-10 flex items-center justify-between px-5 py-3.5 border-b ${theme.border} bg-inherit rounded-t-2xl`}>
            <span className={`text-sm font-semibold ${theme.text}`}>Program Flyer</span>
            <div className="flex items-center gap-2">
              <button
                onClick={handleCopy}
                className={`flex items-center gap-1.5 px-3 py-1.5 border ${theme.border} rounded-lg text-xs font-medium ${theme.text} ${theme.cardHover} transition`}
              >
                {copied ? <Check size={13} className="text-green-500" /> : <Copy size={13} />}
                {copied ? 'Copied' : 'Copy Link'}
              </button>
              <button
                onClick={() => window.print()}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700 transition"
              >
                <Printer size={13} /> Print / Save PDF
              </button>
              <button onClick={onClose} className={`p-1.5 rounded-full ${theme.cardHover} transition`} aria-label="Close">
                <X size={17} className={theme.textMuted} />
              </button>
            </div>
          </div>

          {/* ── Flyer body ─────────────────────────────────────────────────── */}
          <div className="flyer-print-root" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif' }}>

            {/* Hero */}
            <div style={{ position: 'relative', background: accent, overflow: 'hidden' }}>
              {data.imageUrl && (
                <img
                  src={data.imageUrl}
                  alt={title}
                  style={{ width: '100%', height: '200px', objectFit: 'cover', display: 'block', opacity: 0.35 }}
                />
              )}
              <div style={{
                padding: data.imageUrl ? '0 28px 28px' : '28px',
                position: data.imageUrl ? 'absolute' : 'relative',
                bottom: 0, left: 0, right: 0,
              }}>
                {/* Academy name */}
                <div style={{ fontSize: '11px', fontWeight: 700, letterSpacing: '1px', textTransform: 'uppercase', color: 'rgba(255,255,255,0.75)', marginBottom: '10px' }}>
                  {academyName}
                </div>
                {/* Badges */}
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
                  {sport && (
                    <span style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      {sport}
                    </span>
                  )}
                  {level && (
                    <span style={{ background: 'rgba(255,255,255,0.15)', color: '#fff', fontSize: '11px', fontWeight: 600, padding: '3px 10px', borderRadius: '20px', border: '1px solid rgba(255,255,255,0.25)' }}>
                      {level}
                    </span>
                  )}
                  <span style={{
                    background: regOpen ? 'rgba(34,197,94,0.3)' : 'rgba(239,68,68,0.3)',
                    color: regOpen ? '#bbf7d0' : '#fecaca',
                    fontSize: '11px', fontWeight: 700, padding: '3px 10px', borderRadius: '20px',
                    border: `1px solid ${regOpen ? 'rgba(34,197,94,0.4)' : 'rgba(239,68,68,0.4)'}`,
                    textTransform: 'uppercase', letterSpacing: '0.5px',
                  }}>
                    {regOpen ? 'Registration Open' : 'Registration Closed'}
                  </span>
                </div>
                {/* Title */}
                <h2 style={{ margin: 0, fontSize: '26px', fontWeight: 800, color: '#fff', lineHeight: 1.2, textShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                  {title}
                </h2>
              </div>
            </div>

            {/* Content */}
            <div style={{ padding: '24px 28px' }}>

              {/* Description */}
              {data.description && (
                <p style={{ margin: '0 0 20px', fontSize: '14px', color: '#475569', lineHeight: 1.65, paddingLeft: '14px', borderLeft: `3px solid ${accent}` }}>
                  {data.description}
                </p>
              )}

              {/* Details */}
              <div style={{ marginBottom: '24px' }}>
                <DetailRow label="Program Dates"     value={dateRange} />
                <DetailRow label="Daily Schedule"    value={schedule} />
                <DetailRow label="Age Group"         value={data.ageGroup} />
                <DetailRow label="Monthly Fee"       value={fee} />
                <DetailRow label="Reg. Deadline"     value={regDeadline} />
                <DetailRow label="Late Fee"          value={lateFee} />
                <DetailRow label="Capacity"          value={capacity} />
                <DetailRow label="Sessions"          value={data.classes?.length ? `${data.classes.length} session${data.classes.length !== 1 ? 's' : ''}` : null} />
              </div>

              {/* CTA */}
              <div style={{ background: 'linear-gradient(135deg, #1e40af 0%, #2563eb 100%)', borderRadius: '14px', padding: '22px 24px', textAlign: 'center', marginBottom: '20px' }}>
                <p style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: 800, color: '#fff' }}>
                  {regOpen ? 'Ready to join?' : 'Interested in this program?'}
                </p>
                <p style={{ margin: '0 0 16px', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
                  {regOpen ? 'Spots are limited — register today.' : 'Contact us to join the waitlist.'}
                </p>
                <a
                  href={ctaUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', color: '#1e40af', fontSize: '13px', fontWeight: 700, padding: '11px 28px', borderRadius: '8px', textDecoration: 'none', letterSpacing: '0.2px' }}
                >
                  {regOpen ? 'Register Now' : 'Learn More'}
                  <ExternalLink size={13} />
                </a>
              </div>

              {/* Footer */}
              <div style={{ borderTop: '1px solid #e2e8f0', paddingTop: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                <span style={{ fontSize: '13px', fontWeight: 800, color: '#1e293b' }}>{academyName}</span>
                {(contactName || contactEmail || contactPhone || academyWebsite) && (
                  <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'right', lineHeight: 1.8 }}>
                    {contactName    && <div>{contactName}</div>}
                    {contactEmail   && <div>{contactEmail}</div>}
                    {contactPhone   && <div>{contactPhone}</div>}
                    {academyWebsite && <div>{academyWebsite}</div>}
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* end flyer body */}
        </div>
      </div>
    </>
  );
}
